import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JTable } from '@atoms/JTable';
import UserRow from '../UserRow';
import { Box, Stack, useTheme } from '@mui/material';
import { JTablePagination } from '@atoms/JTablePagination';
import { useGetUsers } from '../../queries/useGetUsers';
import TableColumn from '@models/table-column';
import { KEY_ORDER } from '@models/key-order';
import useUsers from '../../hooks/useUsers';
import { JSelect } from '@atoms/JSelect';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import { useDebouncedCallback } from 'use-debounce';
import IOption from '@models/option';
import { SearchTextField } from '@atoms/SearchTextField';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import { useGetEstablishmentsSuggestions } from '@/modules/establishment/queries/useGetEstablishment';
import { FunnelIcon } from '@heroicons/react/24/solid';
import { IconSize } from '@constants';
import {
  FilterButtonContainerStyled,
  FilterCounterStyled,
  FilterIconBox,
  FilterResetStyled,
} from './users-list.styled';
import useJDialog from '@/hooks/useJDialog';
import FilterUsersDialogBody from '../FilterUsersDialogBody';
import { XMarkIcon } from '@heroicons/react/24/outline';

const USERS_PER_PAGE = 20;

const USRES_DEFAULT_ORDER = {
  order: KEY_ORDER.DESC,
  orderBy: 'user.deleted_at',
};
const USERS_TABLE_COLS: TableColumn[] = [
  {
    name: 'USER_FIRSTNAME',
    orderable: true,
    sortingField: 'user.firstname',
  },
  {
    name: 'USER_LASTNAME',
    orderable: true,
    sortingField: 'user.lastname',
  },
  {
    name: 'ENTERPRISE_NAME',
    orderable: true,
    sortingField: 'enterprise_name',
  },
  { name: 'USER_EMAIL', orderable: true, sortingField: 'user.email' },

  { name: 'USER_PHONE', orderable: true, sortingField: 'user_phone' },
  { name: 'STATUS', orderable: true, sortingField: 'status' },
  {
    name: 'ESTABLISHMENTS_COUNT',
    orderable: true,
    sortingField: 'establishmentscount',
  },
];

const UsersList: React.FC = () => {
  const { t } = useTranslation();
  const { setUsersCount } = useUsers();
  const theme = useTheme();
  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>([]);
  const [page, setPage] = useState<number>();
  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');

  const [establishmentInputValue, setEstablishmentInputValue] =
    useState<string>('');
  const [siretInputValue, setSiretInputValue] = useState<string>('');
  const [sirenInputValue, setSirenInputValue] = useState<string>('');
  const [fullNameInputValue, setFullNameInputValue] = useState<string>('');
  const { showJDialog, closeJDialog } = useJDialog();

  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(USRES_DEFAULT_ORDER);

  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );
  const { data: establishments } = useGetEstablishmentsSuggestions(
    10,
    1,
    establishmentInputValue,
  );
  const { data } = useGetUsers(
    {
      page,
      limit: USERS_PER_PAGE,
      order: order.order,
      orderBy: order.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
  );
  const appendSearchFilter = (
    field: FilterCriteriaFields,
    value: string,
    label?: string,
  ): void => {
    const existed = searchFilter?.some((item) => item.field === field);

    if (!existed) setSearchFilter([...searchFilter, { field, value, label }]);
    if (value === '')
      setSearchFilter((prevValue) =>
        prevValue.filter((item) => item.field !== field),
      );
    else
      setSearchFilter((prevValue) =>
        prevValue.map((item) =>
          item.field === field ? { field, value, label } : item,
        ),
      );
  };
  const rows = data?.data.data;
  const meta = data?.data.meta;
  const pagesCount = data?.data.meta.itemCount;
  const isNotFound = rows?.length === 0;

  useEffect(() => {
    setUsersCount(meta?.itemCount);
  }, [meta?.itemCount]);

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const establishmentsOptions = establishments
    ? establishments.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const debouncedAppendSearchFilter = useDebouncedCallback(
    appendSearchFilter,
    500,
  );
  const getDefaultValue = (
    field: FilterCriteriaFields,
    values: ISearchFilter[] = searchFilter,
  ): ISearchFilter | undefined => {
    return values.find((item) => item.field === field);
  };
  const resetFilters = () => {
    setSearchFilter([]);
    setSirenInputValue('');
    setSiretInputValue('');
    setFullNameInputValue('');
  };

  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JSelect
          value={
            searchFilter.find(
              (item) => item.field === FilterCriteriaFields.USER_STATUS,
            )?.value ?? ''
          }
          options={[
            { label: 'Tous', value: '' },
            { label: 'Active', value: 'true' },
            { label: 'Inactive', value: 'false' },
          ]}
          size="small"
          label="Statut"
          onChange={(event) =>
            appendSearchFilter(
              FilterCriteriaFields.USER_STATUS,
              event.target.value as string,
            )
          }
        />

        <SearchTextField
          value={fullNameInputValue}
          label={t('FULL_NAME') ?? undefined}
          onChange={(value) => {
            setFullNameInputValue(value);
            debouncedAppendSearchFilter(
              FilterCriteriaFields.USER_FULLNAME,
              value,
            );
          }}
        />

        <JAutocomplete
          value={{ label: establishmentInputValue, value: '' }}
          label={t('ESTABLISHMENT')}
          options={establishmentsOptions}
          onChange={(ev, newValue) => {
            appendSearchFilter(
              FilterCriteriaFields.ESTABLISHMENT_ID,
              (newValue as IOption)?.value || '',
              (newValue as IOption)?.label || '',
            );
            setEstablishmentInputValue((newValue as IOption)?.label || '');
          }}
          onTextChange={(val) => setEstablishmentInputValue(val)}
        />

        <JAutocomplete
          value={{ label: enterpriseInputValue, value: '' }}
          label={t('ENTERPRISE_NAME')}
          options={enterprisesOptions}
          onChange={(ev, newValue) => {
            appendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_ID,
              (newValue as IOption)?.value || '',
              (newValue as IOption)?.label || '',
            );
            setEnterpriseInputValue((newValue as IOption)?.label || '');
          }}
          onTextChange={(val) => setEnterpriseInputValue(val)}
        />

        <SearchTextField
          value={siretInputValue}
          label={t('SIRET') ?? undefined}
          onChange={(value) => {
            setSiretInputValue(value);
            debouncedAppendSearchFilter(
              FilterCriteriaFields.ESTABLISHMENT_SIRET,
              value,
            );
          }}
        />

        <SearchTextField
          value={sirenInputValue}
          label={t('SIREN') ?? undefined}
          onChange={(value) => {
            setSirenInputValue(value);
            debouncedAppendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_SIREN,
              value,
            );
          }}
        />
        <FilterButtonContainerStyled>
          <FilterIconBox
            attrs={{ active: searchFilter.length > 0 }}
            aria-label="filter"
            data-qa="open-dialog"
            onClick={() => {
              showJDialog(
                t('FILTER_USERS'),
                <FilterUsersDialogBody
                  filters={searchFilter}
                  onApplyFilter={(values) => {
                    closeJDialog();
                    setFullNameInputValue(
                      getDefaultValue(
                        FilterCriteriaFields.USER_FULLNAME,
                        values,
                      )?.value ?? '',
                    );
                    setSiretInputValue(
                      getDefaultValue(
                        FilterCriteriaFields.ESTABLISHMENT_SIRET,
                        values,
                      )?.value ?? '',
                    );
                    setSirenInputValue(
                      getDefaultValue(
                        FilterCriteriaFields.ENTERPRISE_SIREN,
                        values,
                      )?.value ?? '',
                    );
                    setEstablishmentInputValue(
                      getDefaultValue(
                        FilterCriteriaFields.ESTABLISHMENT_ID,
                        values,
                      )?.label ?? '',
                    );
                    setSearchFilter(values);
                  }}
                />,
                'filter',
              );
            }}
          >
            <FunnelIcon
              color={theme.palette.primary.main}
              height={IconSize.buttonIcon}
            />
          </FilterIconBox>
          {searchFilter.length > 0 && (
            <>
              <FilterCounterStyled>{searchFilter.length}</FilterCounterStyled>
              <FilterResetStyled
                data-qa="reset-fitlers"
                onClick={() => resetFilters()}
              >
                <XMarkIcon
                  height={IconSize.xs}
                  color={theme.palette.white.main}
                />
              </FilterResetStyled>
            </>
          )}
        </FilterButtonContainerStyled>
      </Stack>
      <JTable
        isNotFound={isNotFound}
        columns={USERS_TABLE_COLS}
        notFoundLabel={t('NO_USERS_FOUND') ?? ''}
        total={pagesCount ?? 0}
        perpage={USERS_PER_PAGE}
        nbpages={pagesCount ?? 0}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {rows?.map((row) => (
          <UserRow row={row} key={row.user_id} />
        ))}
      </JTable>

      {meta && (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      )}
    </>
  );
};

export default UsersList;
